// Totals (pinned bottom row) support for the grid controller.
//
// Columns with `aggregate: "sum"` in their colDef get summed into a
// pinned bottom row. The first visible column shows "Total".
//
// Usage from the grid controller:
//   import * as totals from "../grid/totals"
//
//   connect()         → totals.install(this)
//   disconnect()      → totals.uninstall(this)
//   after changes     → this.refreshTotals()
//   in gridOptions    → pinnedBottomRowData: totals.initialPinnedData(this)

export function install(controller) {
  controller.refreshTotals = () => {
    refreshTotals(controller)
  }

  controller._totals_onRowRemoved = () => controller.refreshTotals()
  controller.element.addEventListener("grid:rowRemoved", controller._totals_onRowRemoved)
}

export function uninstall(controller) {
  controller.element.removeEventListener("grid:rowRemoved", controller._totals_onRowRemoved)
  delete controller.refreshTotals
}

// Returns pinnedBottomRowData for the initial grid options, or undefined
// if no aggregate columns are defined.
export function initialPinnedData(controller) {
  if (!hasTotalColumns(controller.columnDefsValue)) return undefined
  return [computeTotals(controller.columnDefsValue, controller.initialRowData)]
}

// ── Internal ──────────────────────────────────────────────

function totalColumns(columnDefsValue) {
  return columnDefsValue.filter((c) => c.aggregate === "sum").map((c) => c.field)
}

function hasTotalColumns(columnDefsValue) {
  return totalColumns(columnDefsValue).length > 0
}

function computeTotals(columnDefsValue, rowData) {
  const fields = totalColumns(columnDefsValue)
  const totals = { id: "__total__" }
  const firstCol = columnDefsValue[0]
  if (firstCol) totals[firstCol.field] = "Total"

  for (const field of fields) {
    totals[field] = rowData.reduce((sum, row) => {
      const val = parseFloat(row[field])
      return sum + (isNaN(val) ? 0 : val)
    }, 0)
  }

  return totals
}

function refreshTotals(controller) {
  const { gridApi, columnDefsValue } = controller
  const fields = totalColumns(columnDefsValue)
  if (!fields.length || !gridApi) return

  const totals = { id: "__total__" }
  const firstCol = columnDefsValue[0]
  if (firstCol) totals[firstCol.field] = "Total"

  for (const field of fields) {
    let sum = 0

    gridApi.forEachNode((node) => {
      if (node.rowPinned) return
      const val = parseFloat(node.data[field])
      if (!isNaN(val)) sum += val
    })

    totals[field] = sum
  }

  gridApi.setGridOption("pinnedBottomRowData", [totals])
  controller.autoSizeMarkedColumns()
}
